import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Divider,
  CircularProgress,
  Alert,
  Collapse,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Storage as StorageIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  IntegrationInstructions as IntegrationIcon,
  Dashboard as DashboardIcon,
  Description as DescriptionIcon,
  Settings as SettingsIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  Book as BookIcon,
  Domain as DomainIcon,
  Class as SubjectIcon,
  Info as InfoIcon,
  AssignmentInd as AssignmentIndIcon,
  EventNote as EventNoteIcon,
  Gavel as GovernanceIcon,
  AdminPanelSettings as AdminIcon,
  People as PeopleIcon,
  VpnKey as RolesIcon,
  LocationOn as JurisdictionIcon,
  Menu as MenuIcon,
  ExpandLess,
  ExpandMore,
  ChevronLeft,
  ChevronRight,
  WorkOutline as PortfolioIcon,
  MenuBook as MenuBookIcon,
  AccountTree as GraphIcon,
  Map as MapIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import menuSettingsService, { MenuSettings } from '../../services/menuSettingsService';
import { useUiSettings } from '../../contexts/UiSettingsContext';

const DRAWER_WIDTH = 240;
const COLLAPSED_WIDTH = 72;

// Icon mapping for dynamic menu items
const iconMap: { [key: string]: React.ComponentType } = {
  dashboard: DashboardIcon,
  portfolio: PortfolioIcon,
  school: SchoolIcon,
  storage: StorageIcon,
  governance: GovernanceIcon,
  assignment: AssignmentIndIcon,
  business: BusinessIcon,
  category: CategoryIcon,
  book: BookIcon,
  domain: DomainIcon,
  subject: SubjectIcon,
  security: SecurityIcon,
  analytics: AnalyticsIcon,
  integration: IntegrationIcon,
  event: EventNoteIcon,
  info: InfoIcon,
  description: DescriptionIcon,
  settings: SettingsIcon,
};

// Get icon color for specific menu items
const getIconColor = (menuId: string, path: string): string | undefined => {
  const colorMap: { [key: string]: string } = {
    '/admin/documentation': '#003366',
    '/admin/janusgraph': '#f44336',
    '/admin/data-strategy-operations-center': '#2196f3',
    '/admin/automated-processes': '#9c27b0',
    '/portfolio': '#4caf50',
    '/admin/capstone': '#ff9800',
  };
  
  return colorMap[path];
};

// Get icon for menu item based on path or menuId
const getIconForMenuItem = (menuId: string, path: string): React.ComponentType => {
  // Map specific menu items to icons
  const pathIconMap: { [key: string]: React.ComponentType } = {
    '/dashboard': DashboardIcon,
    '/portfolio': PortfolioIcon,
    '/learn101': SchoolIcon,
    '/data-literacy': SchoolIcon,
    '/national-production-dataset': StorageIcon,
    '/data-governance-quality': GovernanceIcon,
    '/e22-classification': AssignmentIndIcon,
    '/data-catalog': StorageIcon,
    '/data-assets': StorageIcon,
    '/assets/business-processes': BusinessIcon,
    '/assets/data-categories': CategoryIcon,
    '/assets/data-concepts': BookIcon,
    '/assets/data-domains': DomainIcon,
    '/assets/subject-categories': SubjectIcon,
    '/asset-types': StorageIcon,
    '/governance/dashboard': DashboardIcon,
    '/governance/council': PeopleIcon,
    '/governance/policies': DescriptionIcon,
    '/governance/quality': AssignmentIndIcon,
    '/governance/compliance': SecurityIcon,
    '/governance/helpdesk': InfoIcon,
    '/strategy/vision': AnalyticsIcon,
    '/strategy/roadmap': MapIcon,
    '/strategy/maturity': TimelineIcon,
    '/strategy/training': SchoolIcon,
    '/business-glossary': MenuBookIcon,
    '/context/business-terms': BookIcon,
    '/context/acronyms': SubjectIcon,
    '/context/reports': DescriptionIcon,
    '/context/bi-reports': AnalyticsIcon,
    '/data-governance': GovernanceIcon,
    '/dgvsmdm': SecurityIcon,
    '/data-steward-lesson': AdminIcon,
    '/analytics': AnalyticsIcon,
    '/study-aids/business-analytics': AnalyticsIcon,
    '/integration': IntegrationIcon,
    '/weekly-status': EventNoteIcon,
    '/monthly-status': EventNoteIcon,
    '/about': InfoIcon,
    '/docs': DescriptionIcon,
    '/settings': SettingsIcon,
    '/access/user-management': PeopleIcon,
    '/access/roles': RolesIcon,
    '/access/jurisdictions': JurisdictionIcon,
    '/admin/menu-management': MenuIcon,
    '/admin/system-settings': SettingsIcon,
    '/admin/uscis-application-tracking': AssignmentIndIcon,
    '/admin/data-strategy-planning': GovernanceIcon,
    '/admin/team-roster': PeopleIcon,
    '/admin/documentation': MenuBookIcon,
    '/admin/janusgraph': GraphIcon,
    '/admin/data-catalog-roadmap': MapIcon,
    '/admin/npd-roadmap': TimelineIcon,
    '/federal-data-strategy': GovernanceIcon,
    '/tasks': AssignmentIndIcon,
    '/admin/capstone': SchoolIcon,
    '/data-steward': AdminIcon,
  };
  
  return pathIconMap[path] || StorageIcon;
};

interface MenuItem {
  text: string;
  icon: React.ComponentType;
  path: string;
  isEnabled: boolean;
  iconColor?: string;
}

const Sidenav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    settings,
    setSidebarCollapsed,
    setKnowledgeOpen,
    setGovernanceOpen,
    setStrategyOpen,
    setDataStewardOpen,
    setAdministrationOpen,
  } = useUiSettings();
  const isCollapsed = settings.sidebarCollapsed;
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [secondaryMenuItems, setSecondaryMenuItems] = useState<MenuItem[]>([]);
  const [contextMenuItems, setContextMenuItems] = useState<MenuItem[]>([]);
  const [knowledgeMenuItems, setKnowledgeMenuItems] = useState<MenuItem[]>([]);
  const [governanceMenuItems, setGovernanceMenuItems] = useState<MenuItem[]>([]);
  const [strategyMenuItems, setStrategyMenuItems] = useState<MenuItem[]>([]);
  const [dataStewardMenuItems, setDataStewardMenuItems] = useState<MenuItem[]>([]);
  const [administrationItems, setAdministrationItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contextOpen, setContextOpen] = useState(false);
  const knowledgeOpen = settings.knowledgeOpen;
  const governanceOpen = settings.governanceOpen;
  const strategyOpen = settings.strategyOpen;
  const dataStewardOpen = settings.dataStewardOpen;
  const administrationOpen = settings.administrationOpen;

  useEffect(() => {
    loadMenuItems();
  }, [user]);

  const loadMenuItems = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading menu items from API...');
      const enabledMenus = await menuSettingsService.getEnabledMenuItems();
      console.log('Received menu items:', enabledMenus.length);
      
      // Filter menu items based on user role
      const filteredMenus = enabledMenus.filter(menu => {
        if (!user) return menu.requiredRole === 'user';
        
        switch (user.role) {
          case 'admin':
          case 'odos-admin':
            return true; // Admin and ODOS-Admin can see everything
          case 'data-steward':
            return ['user', 'data-steward'].includes(menu.requiredRole);
          default:
            return menu.requiredRole === 'user';
        }
      });
      
      // Separate primary, secondary, and administration menu items
      const primary = filteredMenus
        .filter(menu => menu.category === 'primary')
        .sort((a, b) => a.order - b.order)
        .map(menu => ({
          text: menu.text,
          icon: getIconForMenuItem(menu.menuId, menu.path),
          path: menu.path,
          isEnabled: menu.isEnabled,
          iconColor: getIconColor(menu.menuId, menu.path)
        }));
        
      const secondary = filteredMenus
        .filter(menu => menu.category === 'secondary')
        .sort((a, b) => a.order - b.order)
        .map(menu => ({
          text: menu.text,
          icon: getIconForMenuItem(menu.menuId, menu.path),
          path: menu.path,
          isEnabled: menu.isEnabled,
          iconColor: getIconColor(menu.menuId, menu.path)
        }));
      // Guarantee access to the Documentation Center even if menu settings are missing
      const hasDocsLink = secondary.some(item => item.path === '/docs');
      const secondaryWithDocs = hasDocsLink
        ? secondary
        : ([{ text: 'Documentation', icon: DescriptionIcon, path: '/docs', isEnabled: true }] as MenuItem[]).concat(secondary);
        
      const context = filteredMenus
        .filter(menu => menu.category === 'context')
        .sort((a, b) => a.order - b.order)
        .map(menu => ({
          text: menu.text,
          icon: getIconForMenuItem(menu.menuId, menu.path),
          path: menu.path,
          isEnabled: menu.isEnabled,
          iconColor: getIconColor(menu.menuId, menu.path)
        }));
        
      const knowledge = filteredMenus
        .filter(menu => menu.category === 'knowledge')
        .sort((a, b) => a.order - b.order)
        .map(menu => ({
          text: menu.text,
          icon: getIconForMenuItem(menu.menuId, menu.path),
          path: menu.path,
          isEnabled: menu.isEnabled,
          iconColor: getIconColor(menu.menuId, menu.path)
        }));
        
      const governance = filteredMenus
        .filter(menu => menu.category === 'governance')
        .filter(menu => {
          // Filter based on required role
          if (!menu.requiredRole || menu.requiredRole === 'user') return true;
          if (menu.requiredRole === 'data-steward') return user?.role === 'data-steward' || user?.role === 'admin' || user?.role === 'odos-admin';
          if (menu.requiredRole === 'admin') return user?.role === 'admin' || user?.role === 'odos-admin';
          return false;
        })
        .sort((a, b) => a.order - b.order)
        .map(menu => ({
          text: menu.text,
          icon: getIconForMenuItem(menu.menuId, menu.path),
          path: menu.path,
          isEnabled: menu.isEnabled,
          iconColor: getIconColor(menu.menuId, menu.path)
        }));
        
      const dataStrategy = filteredMenus
        .filter(menu => menu.category === 'data-strategy')
        .filter(menu => {
          // Filter based on required role
          if (!menu.requiredRole || menu.requiredRole === 'user') return true;
          if (menu.requiredRole === 'data-steward') return user?.role === 'data-steward' || user?.role === 'admin' || user?.role === 'odos-admin';
          if (menu.requiredRole === 'admin') return user?.role === 'admin' || user?.role === 'odos-admin';
          return false;
        })
        .sort((a, b) => a.order - b.order)
        .map(menu => ({
          text: menu.text,
          icon: getIconForMenuItem(menu.menuId, menu.path),
          path: menu.path,
          isEnabled: menu.isEnabled,
          iconColor: getIconColor(menu.menuId, menu.path)
        }));
        
      const dataSteward = filteredMenus
        .filter(menu => menu.category === 'data-steward')
        .filter(menu => {
          // Filter based on required role
          if (!menu.requiredRole || menu.requiredRole === 'user') return true;
          if (menu.requiredRole === 'data-steward') return user?.role === 'data-steward' || user?.role === 'admin' || user?.role === 'odos-admin';
          if (menu.requiredRole === 'admin') return user?.role === 'admin' || user?.role === 'odos-admin';
          return false;
        })
        .sort((a, b) => a.order - b.order)
        .map(menu => ({
          text: menu.text,
          icon: getIconForMenuItem(menu.menuId, menu.path),
          path: menu.path,
          isEnabled: menu.isEnabled,
          iconColor: getIconColor(menu.menuId, menu.path)
        }));
        
      const administration = filteredMenus
        .filter(menu => menu.category === 'administration')
        .sort((a, b) => a.order - b.order)
        .map(menu => ({
          text: menu.text,
          icon: getIconForMenuItem(menu.menuId, menu.path),
          path: menu.path,
          isEnabled: menu.isEnabled,
          iconColor: getIconColor(menu.menuId, menu.path)
        }));
      
      console.log('📚 Knowledge items loaded:', knowledge.length);
      knowledge.forEach(item => console.log(`  - ${item.text}`));
      console.log('🏛️  Governance items loaded:', governance.length);
      governance.forEach(item => console.log(`  - ${item.text}`));
      console.log('📊 Strategy items loaded:', dataStrategy.length);
      dataStrategy.forEach(item => console.log(`  - ${item.text}`));
      console.log('👥 Data Steward items loaded:', dataSteward.length);
      dataSteward.forEach(item => console.log(`  - ${item.text}`));
      
      setMenuItems(primary);
      setSecondaryMenuItems(secondaryWithDocs);
      setContextMenuItems(context);
      setKnowledgeMenuItems(knowledge);
      setGovernanceMenuItems(governance);
      setStrategyMenuItems(dataStrategy);
      setDataStewardMenuItems(dataSteward);
      setAdministrationItems(administration);
    } catch (err) {
      console.error('Failed to load menu items:', err);
      setError('Failed to load menu items');
      // Fallback to default menu items if API fails
      setMenuItems([
        { text: 'Dashboard', icon: DashboardIcon, path: '/dashboard', isEnabled: true },
        { text: 'Portfolio', icon: PortfolioIcon, path: '/portfolio', isEnabled: true },
        { text: 'E-Unify 101', icon: SchoolIcon, path: '/learn101', isEnabled: true },
        { text: 'Data Assets', icon: StorageIcon, path: '/data-assets', isEnabled: true },
      ]);
      setSecondaryMenuItems([
        { text: 'About E-Unify', icon: InfoIcon, path: '/about', isEnabled: true },
        { text: 'Settings', icon: SettingsIcon, path: '/settings', isEnabled: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const isCurrentPath = (path: string) => {
    // Exact match for root path
    if (path === '/') {
      return location.pathname === '/';
    }
    // For other paths, match if current path starts with the menu path
    // This allows sub-routes to highlight their parent menu item
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  if (loading) {
    return (
      <Drawer
        variant="permanent"
        sx={{
          width: isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
            backgroundColor: 'background.paper',
            position: 'static',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
        </Box>
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          backgroundColor: 'background.paper',
          position: 'static',
        },
      }}
    >
      <Box sx={{ overflow: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : 'flex-end',
            px: 1,
            py: 1,
          }}
        >
          <Tooltip title={isCollapsed ? 'Expand navigation menu' : 'Collapse navigation menu'}>
            <IconButton
              size="small"
              onClick={() => setSidebarCollapsed(!isCollapsed)}
              aria-label={isCollapsed ? 'Expand navigation menu' : 'Collapse navigation menu'}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="warning">
              {error}
            </Alert>
          </Box>
        )}
        <List>
          {menuItems.filter(item => item.isEnabled).map((item) => (
            <ListItem key={item.text} disablePadding>
              {isCollapsed ? (
                <Tooltip title={item.text} placement="right">
                  <ListItemButton
                    selected={isCurrentPath(item.path)}
                    onClick={() => navigate(item.path)}
                    sx={{
                      justifyContent: 'center',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                        '& .MuiListItemIcon-root': {
                          color: 'white',
                        },
                      },
                    }}
                    aria-label={item.text}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 'auto',
                        color: isCurrentPath(item.path) ? 'white' : (item.iconColor || 'inherit'),
                      }}
                    >
                      <item.icon />
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              ) : (
                <ListItemButton
                  selected={isCurrentPath(item.path)}
                  onClick={() => navigate(item.path)}
                  sx={{
                    justifyContent: 'flex-start',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'white',
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: isCurrentPath(item.path) ? 'white' : (item.iconColor || 'inherit'),
                    }}
                  >
                    <item.icon />
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              )}
            </ListItem>
          ))}
        </List>
        
        {/* Context Section (Legacy - will be removed after migration) */}
        {contextMenuItems.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => setContextOpen(!contextOpen)}
                sx={{
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  backgroundColor: 'rgba(0, 51, 102, 0.05)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 51, 102, 0.1)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : 40 }}>
                  <MenuBookIcon sx={{ color: '#003366' }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#003366' }}>
                        Context
                      </Typography>
                    } 
                  />
                )}
                {!isCollapsed && (contextOpen ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>
            </ListItem>
            <Collapse in={contextOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {contextMenuItems.filter(item => item.isEnabled).map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      selected={isCurrentPath(item.path)}
                      onClick={() => navigate(item.path)}
                      sx={{
                        pl: 4,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                          '& .MuiListItemIcon-root': {
                            color: 'white',
                          },
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          color: isCurrentPath(item.path) ? 'white' : (item.iconColor || 'inherit'),
                        }}
                      >
                        <item.icon />
                      </ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
        
        {/* Knowledge & Reference Section */}
        {knowledgeMenuItems.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <ListItem disablePadding>
              <Tooltip title="Knowledge & Reference" placement="right">
                <ListItemButton
                  onClick={() => setKnowledgeOpen(!knowledgeOpen)}
                  sx={{
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    backgroundColor: 'rgba(0, 51, 102, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 51, 102, 0.1)',
                    },
                  }}
                  aria-label="Knowledge & Reference section"
                >
                  <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : 40 }}>
                    <MenuBookIcon sx={{ color: '#003366' }} />
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText 
                      primary={
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#003366' }}>
                          Knowledge & Reference
                        </Typography>
                      } 
                    />
                  )}
                  {!isCollapsed && (knowledgeOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            <Collapse in={knowledgeOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {knowledgeMenuItems.filter(item => item.isEnabled).map((item) => (
                  <ListItem key={item.text} disablePadding>
                    {isCollapsed ? (
                      <Tooltip title={item.text} placement="right">
                        <ListItemButton
                          selected={isCurrentPath(item.path)}
                          onClick={() => navigate(item.path)}
                          sx={{
                            pl: 0,
                            justifyContent: 'center',
                            '&.Mui-selected': {
                              backgroundColor: 'primary.main',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'primary.dark',
                              },
                              '& .MuiListItemIcon-root': {
                                color: 'white',
                              },
                            },
                          }}
                          aria-label={item.text}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 'auto',
                              color: isCurrentPath(item.path) ? 'white' : (item.iconColor || 'inherit'),
                            }}
                          >
                            <item.icon />
                          </ListItemIcon>
                        </ListItemButton>
                      </Tooltip>
                    ) : (
                      <ListItemButton
                        selected={isCurrentPath(item.path)}
                        onClick={() => navigate(item.path)}
                        sx={{
                          pl: 4,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            },
                            '& .MuiListItemIcon-root': {
                              color: 'white',
                            },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 40,
                            color: isCurrentPath(item.path) ? 'white' : (item.iconColor || 'inherit'),
                          }}
                        >
                          <item.icon />
                        </ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItemButton>
                    )}
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
        
        {/* Data Governance Section */}
        {governanceMenuItems.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <ListItem disablePadding>
              <Tooltip title="Data Governance" placement="right">
                <ListItemButton
                  onClick={() => setGovernanceOpen(!governanceOpen)}
                  sx={{
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    backgroundColor: 'rgba(0, 51, 102, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 51, 102, 0.1)',
                    },
                  }}
                  aria-label="Data Governance section"
                >
                  <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : 40 }}>
                    <GovernanceIcon sx={{ color: '#003366' }} />
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText 
                      primary={
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#003366' }}>
                          Data Governance
                        </Typography>
                      } 
                    />
                  )}
                  {!isCollapsed && (governanceOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            <Collapse in={governanceOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {governanceMenuItems.filter(item => item.isEnabled).map((item) => (
                  <ListItem key={item.text} disablePadding>
                    {isCollapsed ? (
                      <Tooltip title={item.text} placement="right">
                        <ListItemButton
                          selected={isCurrentPath(item.path)}
                          onClick={() => navigate(item.path)}
                          sx={{
                            pl: 0,
                            justifyContent: 'center',
                            '&.Mui-selected': {
                              backgroundColor: 'primary.main',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'primary.dark',
                              },
                              '& .MuiListItemIcon-root': {
                                color: 'white',
                              },
                            },
                          }}
                          aria-label={item.text}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 'auto',
                              color: isCurrentPath(item.path) ? 'white' : (item.iconColor || 'inherit'),
                            }}
                          >
                            <item.icon />
                          </ListItemIcon>
                        </ListItemButton>
                      </Tooltip>
                    ) : (
                      <ListItemButton
                        selected={isCurrentPath(item.path)}
                        onClick={() => navigate(item.path)}
                        sx={{
                          pl: 4,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            },
                            '& .MuiListItemIcon-root': {
                              color: 'white',
                            },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 40,
                            color: isCurrentPath(item.path) ? 'white' : (item.iconColor || 'inherit'),
                          }}
                        >
                          <item.icon />
                        </ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItemButton>
                    )}
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
        
        {/* Data Strategy Section */}
        {strategyMenuItems.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <ListItem disablePadding>
              <Tooltip title="Data Strategy" placement="right">
                <ListItemButton
                  onClick={() => setStrategyOpen(!strategyOpen)}
                  sx={{
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    backgroundColor: 'rgba(0, 51, 102, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 51, 102, 0.1)',
                    },
                  }}
                  aria-label="Data Strategy section"
                >
                  <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : 40 }}>
                    <AnalyticsIcon sx={{ color: '#003366' }} />
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText 
                      primary={
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#003366' }}>
                          Data Strategy
                        </Typography>
                      } 
                    />
                  )}
                  {!isCollapsed && (strategyOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            <Collapse in={strategyOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {strategyMenuItems.filter(item => item.isEnabled).map((item) => (
                  <ListItem key={item.text} disablePadding>
                    {isCollapsed ? (
                      <Tooltip title={item.text} placement="right">
                        <ListItemButton
                          selected={isCurrentPath(item.path)}
                          onClick={() => {
                            navigate(item.path);
                          }}
                          sx={{
                            pl: 0,
                            justifyContent: 'center',
                            '&.Mui-selected': {
                              backgroundColor: 'primary.main',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'primary.dark',
                              },
                              '& .MuiListItemIcon-root': {
                                color: 'white',
                              },
                            },
                          }}
                          aria-label={item.text}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 'auto',
                              color: isCurrentPath(item.path) ? 'white' : (item.iconColor || 'inherit'),
                            }}
                          >
                            <item.icon />
                          </ListItemIcon>
                        </ListItemButton>
                      </Tooltip>
                    ) : (
                      <ListItemButton
                        selected={isCurrentPath(item.path)}
                        onClick={() => navigate(item.path)}
                        sx={{
                          pl: 4,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            },
                            '& .MuiListItemIcon-root': {
                              color: 'white',
                            },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 40,
                            color: isCurrentPath(item.path) ? 'white' : (item.iconColor || 'inherit'),
                          }}
                        >
                          <item.icon />
                        </ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItemButton>
                    )}
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
        
        {/* Data Steward Section */}
        {dataStewardMenuItems.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <ListItem disablePadding>
              <Tooltip title="Data Steward" placement="right">
                <ListItemButton
                  onClick={() => setDataStewardOpen(!dataStewardOpen)}
                  sx={{
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    backgroundColor: 'rgba(0, 51, 102, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 51, 102, 0.1)',
                    },
                  }}
                  aria-label="Data Steward section"
                >
                  <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : 40 }}>
                    <AdminIcon sx={{ color: '#003366' }} />
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText 
                      primary={
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#003366' }}>
                          Data Steward
                        </Typography>
                      } 
                    />
                  )}
                  {!isCollapsed && (dataStewardOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            <Collapse in={dataStewardOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {dataStewardMenuItems.filter(item => item.isEnabled).map((item) => (
                  <ListItem key={item.text} disablePadding>
                    {isCollapsed ? (
                      <Tooltip title={item.text} placement="right">
                        <ListItemButton
                          selected={isCurrentPath(item.path)}
                          onClick={() => {
                            navigate(item.path);
                          }}
                          sx={{
                            pl: 0,
                            justifyContent: 'center',
                            '&.Mui-selected': {
                              backgroundColor: 'primary.main',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'primary.dark',
                              },
                              '& .MuiListItemIcon-root': {
                                color: 'white',
                              },
                            },
                          }}
                          aria-label={item.text}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 'auto',
                              color: isCurrentPath(item.path) ? 'white' : (item.iconColor || 'inherit'),
                            }}
                          >
                            <item.icon />
                          </ListItemIcon>
                        </ListItemButton>
                      </Tooltip>
                    ) : (
                      <ListItemButton
                        selected={isCurrentPath(item.path)}
                        onClick={() => {
                          navigate(item.path);
                        }}
                        sx={{
                          pl: 4,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            },
                            '& .MuiListItemIcon-root': {
                              color: 'white',
                            },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 40,
                            color: isCurrentPath(item.path) ? 'white' : (item.iconColor || 'inherit'),
                          }}
                        >
                          <item.icon />
                        </ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItemButton>
                    )}
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
        
        {/* Administration Section */}
        {administrationItems.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <ListItem disablePadding>
              <Tooltip title="Administration" placement="right">
                <ListItemButton
                  onClick={() => setAdministrationOpen(!administrationOpen)}
                  sx={{
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    backgroundColor: 'rgba(0, 51, 102, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 51, 102, 0.1)',
                    },
                  }}
                  aria-label="Administration section"
                >
                  <ListItemIcon sx={{ minWidth: isCollapsed ? 'auto' : 40 }}>
                    <AdminIcon sx={{ color: '#003366' }} />
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText 
                      primary={
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#003366' }}>
                          Administration
                        </Typography>
                      } 
                    />
                  )}
                  {!isCollapsed && (administrationOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            <Collapse in={administrationOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {administrationItems.filter(item => item.isEnabled).map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      selected={isCurrentPath(item.path)}
                      onClick={() => {
                        navigate(item.path);
                      }}
                      sx={{
                        pl: 4,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                          '& .MuiListItemIcon-root': {
                            color: 'white',
                          },
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          color: isCurrentPath(item.path) ? 'white' : (item.iconColor || 'inherit'),
                        }}
                      >
                        <item.icon />
                      </ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
        
        <Divider sx={{ my: 2 }} />
        <List>
          {secondaryMenuItems.filter(item => item.isEnabled).map((item) => (
            <ListItem key={item.text} disablePadding>
              {isCollapsed ? (
                <Tooltip title={item.text} placement="right">
                  <ListItemButton
                    selected={isCurrentPath(item.path)}
                    onClick={() => navigate(item.path)}
                    sx={{
                      justifyContent: 'center',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                        '& .MuiListItemIcon-root': {
                          color: 'white',
                        },
                      },
                    }}
                    aria-label={item.text}
                  >
                    <ListItemIcon 
                      sx={{ 
                        minWidth: 'auto',
                        color: isCurrentPath(item.path) ? 'white' : (item.iconColor || 'inherit'),
                      }}
                    >
                      <item.icon />
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              ) : (
                <ListItemButton
                  selected={isCurrentPath(item.path)}
                  onClick={() => navigate(item.path)}
                  sx={{
                    justifyContent: 'flex-start',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'white',
                      },
                    },
                  }}
                >
                  <ListItemIcon 
                    sx={{ 
                      minWidth: 40,
                      color: isCurrentPath(item.path) ? 'white' : (item.iconColor || 'inherit'),
                    }}
                  >
                    <item.icon />
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              )}
            </ListItem>
          ))}
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidenav;
